import { useTranslation } from "react-i18next";

interface MetricCardProps {
  module?: string;
  label: string;
  value: string;
  subLabel?: string;
  dateRange?: string;
  status?: "on-target" | "below-target" | "above-target";
  tooltip?: string;
  detailRows?: { label: string; value: string }[];
  compact?: boolean;
  className?: string;
}

export default function MetricCard({
  module,
  label,
  value,
  subLabel,
  dateRange,
  status,
  tooltip,
  detailRows,
  compact,
  className,
}: MetricCardProps) {
  const { t } = useTranslation();

  const statusConfig = {
    "on-target": {
      label: t("common.onTarget"),
      color: "#3CB05C",
      bg: "rgba(60,176,92,0.12)",
    },
    "below-target": {
      label: t("common.belowTarget"),
      color: "#D9338C",
      bg: "rgba(217,51,140,0.12)",
    },
    "above-target": {
      label: t("common.onTarget"),
      color: "#3CB05C",
      bg: "rgba(60,176,92,0.12)",
    },
  };

  const cfg = status ? statusConfig[status] : null;

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg flex flex-col ${
        compact ? "py-2.5 px-4 gap-1" : "p-4 gap-1 flex-1"
      } ${className ?? ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          {module && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
              {module}
            </span>
          )}
          <span className="text-xs text-gray-500 whitespace-nowrap">{label}</span>
          <span className="relative group w-3.5 h-3.5 flex items-center justify-center cursor-pointer shrink-0">
            <i className="ri-information-line text-xs text-gray-400" />
            {tooltip && (
              <div className="absolute left-5 top-0 z-50 hidden group-hover:block w-72 bg-gray-800 text-white text-xs rounded-lg p-2.5 leading-relaxed shadow-lg pointer-events-none">
                {tooltip}
                <div className="absolute left-[-4px] top-2 w-2 h-2 bg-gray-800 rotate-45" />
              </div>
            )}
          </span>
        </div>
        {cfg && (
          <span
            className="text-xs font-medium whitespace-nowrap shrink-0 ml-2 px-2.5 py-0.5 rounded-full"
            style={{ color: cfg.color, backgroundColor: cfg.bg }}
          >
            {cfg.label}
          </span>
        )}
      </div>

      <div
        className="font-bold text-2xl mt-0.5"
        style={{ color: value === "0" ? "#9CA3AF" : "#D9338C" }}
      >
        {value}
      </div>

      {subLabel && <div className="text-xs text-gray-400 mt-0.5">{subLabel}</div>}

      {detailRows && detailRows.length > 0 && (
        <div className="flex flex-col gap-y-0.5 mt-1">
          {detailRows.map((row, i) => (
            <div key={i} className="text-xs flex items-center gap-1">
              <span className="text-gray-400">{row.label}</span>
              <span className="text-gray-700 font-medium">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {dateRange && (
        <div className="flex items-center gap-1 mt-0.5 whitespace-nowrap">
          <i className="ri-calendar-line text-xs text-gray-400 shrink-0" />
          <span className="text-xs text-gray-400">{dateRange}</span>
        </div>
      )}
    </div>
  );
}
